/**
 * Persists marketing/tracking query params across in-app navigation (sessionStorage + URL),
 * so they remain available for forms and analytics. Skips routes that use their own query API (search).
 * Call from each site's gatsby-browser.js `onInitialClientRender`.
 */

let persistentParams = {};

const pathnameMatchesPersistentParamsExclusion = (pathname) => {
  if (!pathname) return false;
  if (pathname.includes("/search/")) return true;
  return pathname.endsWith("/search");
};

const getInitialParams = () => {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const urlParams = new URLSearchParams(window.location.search);
  const params = {};

  for (const [key, value] of urlParams.entries()) {
    params[key] = value;
  }

  const storedParams = JSON.parse(
    sessionStorage.getItem("persistent_params") || "{}",
  );

  if (pathnameMatchesPersistentParamsExclusion(pathname)) {
    return Object.keys(storedParams).length ? storedParams : params;
  }

  if (Object.keys(params).length === 0) {
    return storedParams;
  }

  return params;
};

const saveParamsToSession = (params) => {
  sessionStorage.setItem("persistent_params", JSON.stringify(params));
};

const injectParamsIntoUrl = (url) => {
  if (!persistentParams || Object.keys(persistentParams).length === 0) {
    return url;
  }

  let tempUrl;
  try {
    tempUrl = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url, window.location.origin);
  } catch (e) {
    console.error("Invalid URL:", url, e);
    return url;
  }

  if (pathnameMatchesPersistentParamsExclusion(tempUrl.pathname)) {
    return url;
  }

  for (const key in persistentParams) {
    tempUrl.searchParams.set(key, persistentParams[key]);
  }

  return tempUrl.toString();
};

const patchHistoryMethod = (type) => {
  const original = history[type];
  history[type] = function (...args) {
    if (typeof args[2] === "string") {
      args[2] = injectParamsIntoUrl(args[2]);
    }
    return original.apply(this, args);
  };
};

const patchLinkClicks = () => {
  document.addEventListener("click", (e) => {
    const anchor = e.target.closest("a[href]");
    if (!anchor || !document.contains(anchor)) return;

    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) {
      return;
    }

    if (href.startsWith("http") || href.startsWith("/")) {
      const modifiedHref = injectParamsIntoUrl(href);
      anchor.href = modifiedHref;
    }
  });
};

const patchWindowOpen = () => {
  const originalWindowOpen = window.open;
  window.open = function (url, target, features) {
    if (typeof url === "string" && (target === "_blank" || !target)) {
      const newUrl = injectParamsIntoUrl(url);
      return originalWindowOpen.call(window, newUrl, target, features);
    }
    return originalWindowOpen.apply(window, arguments);
  };
};

export function installPersistentUrlParams() {
  if (typeof window === "undefined") return;

  persistentParams = getInitialParams();
  saveParamsToSession(persistentParams);

  patchHistoryMethod("pushState");
  patchHistoryMethod("replaceState");
  patchLinkClicks();
  patchWindowOpen();

  window.addEventListener("popstate", () => {
    persistentParams = getInitialParams();
    saveParamsToSession(persistentParams);
  });

  if (!pathnameMatchesPersistentParamsExclusion(window.location.pathname)) {
    const url = new URL(window.location.href);
    let needsUpdate = false;

    for (const key in persistentParams) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, persistentParams[key]);
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }
}
